import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

const Login: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [loginMode, setLoginMode] = useState<'email' | 'oauth'>('oauth');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    if (!isLoading) {
      login('google');
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        // Store access token
        localStorage.setItem('accessToken', response.data.accessToken);

        // Redirect to home
        window.location.href = '/';
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, {
        email
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        setEmail(''); // Clear email field
      }
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-canvas flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Organic lavender/blue blob decorations behind the card — Midnight &
          Lavender chrome reference. Purely decorative, non-interactive. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-[45%_55%_60%_40%/50%_45%_55%_50%] bg-accent opacity-20 blur-3xl" />
        <div className="absolute top-1/3 -right-32 h-[28rem] w-[28rem] rounded-[55%_45%_50%_50%/40%_55%_45%_60%] bg-[#5A9CFF] opacity-20 blur-3xl" />
        <div className="absolute -bottom-32 left-1/4 h-80 w-80 rounded-[50%_50%_45%_55%/55%_45%_55%_45%] bg-accent opacity-10 blur-3xl" />
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src="/eskimi-logo-medium.png"
            alt="Eskimi Logo"
            className="h-12 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-ink">
          Welcome to Eskimi DeepContext
        </h2>
        <p className="mt-2 text-center text-sm text-ink-muted">
          Take Control of Every URL - Advanced Content Safety Analysis for Programmatic Advertising
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Login Mode Tabs */}
          <div className="mb-6">
            <nav className="inline-flex w-full rounded-full bg-ghost-bg p-[3px]" aria-label="Tabs">
              <Button
                type="button"
                variant="pill"
                className="flex-1"
                active={loginMode === 'email'}
                onClick={() => setLoginMode('email')}
              >
                Email & Password
              </Button>
              <Button
                type="button"
                variant="pill"
                className="flex-1"
                active={loginMode === 'oauth'}
                onClick={() => setLoginMode('oauth')}
              >
                SSO / OAuth
              </Button>
            </nav>
          </div>

          {/* Email Login Form */}
          {loginMode === 'email' && !showForgotPassword && (
            <form onSubmit={handleEmailLogin} className="space-y-6">
              {error && (
                <div className="rounded-md bg-risk-bg p-4">
                  <div className="text-sm text-risk">{error}</div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ink">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-line rounded-md shadow-sm placeholder-ink-muted focus:outline-none focus:ring-accent focus:border-accent sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-ink">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-line rounded-md shadow-sm placeholder-ink-muted focus:outline-none focus:ring-accent focus:border-accent sm:text-sm"
                  />
                </div>
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-accent hover:opacity-80 font-medium"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 rounded-full shadow-sm text-sm font-medium text-white bg-gradient-accent hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-3"></div>
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Forgot Password Form */}
          {loginMode === 'email' && showForgotPassword && (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              {error && (
                <div className="rounded-md bg-risk-bg p-4">
                  <div className="text-sm text-risk">{error}</div>
                </div>
              )}

              {success && (
                <div className="rounded-md bg-safe-bg p-4">
                  <div className="text-sm text-safe">{success}</div>
                </div>
              )}

              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-ink">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="forgot-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="appearance-none block w-full px-3 py-2 border border-line rounded-md shadow-sm placeholder-ink-muted focus:outline-none focus:ring-accent focus:border-accent sm:text-sm"
                  />
                </div>
                <p className="mt-2 text-sm text-ink-muted">
                  We'll send you a link to reset your password.
                </p>
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 rounded-full shadow-sm text-sm font-medium text-white bg-gradient-accent hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-3"></div>
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setError('');
                    setSuccess('');
                    setEmail('');
                  }}
                  className="w-full flex justify-center py-2 px-4 border border-line rounded-md shadow-sm text-sm font-medium text-ink bg-surface hover:bg-canvas focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}

          {/* OAuth Login */}
          {loginMode === 'oauth' && (
            <div className="space-y-6">
              <div className="mb-4 bg-warn-bg rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-warn" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-bold text-warn">
                      Access Request Required
                    </h3>
                    <div className="mt-2 text-sm text-warn">
                      <p className="font-semibold">
                        Google login alone will NOT grant you access to this application.
                      </p>
                      <p className="mt-1">
                        You must first request a user account from your administrator. Only pre-approved email addresses can access DeepContext.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-center text-ink-muted">
                  Sign in with your Google account to access the brand safety analysis tools.
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-line rounded-md shadow-sm text-sm font-medium text-ink bg-surface hover:bg-canvas focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ink-muted mr-3"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-ink-muted">
            By signing in, you agree to access this tool using your credentials.
            <br />
            Your account information is used only for authentication.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
